// src/components/coach/TeamJoinLinkCard.jsx
//
// The coach's own team join link, on their team dashboard. A coach can only
// ever see their own team's link, so there is nothing to mix up, and the super
// admin never handles a per-team link at all. Copy it, share it to the team
// group chat, or copy the short "how to join" text that goes with it.
//
// Below it, on request, the team's STAFF link: the join link plus the
// organization's staff code (fetched from getStaffJoinCode, which answers only
// the org's own coaches and admins). Assistants, the manager and other bench
// staff join from it and land on the coaches roster for this team. In a
// workplace the same card reads manager, supervisor and team lead
// (copyDictionary via useOrgVoice); the persisted role stays 'coach'.

import React, { useState } from 'react';
import { Link2, Copy, Share2, Check, ClipboardList, UserCog } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase-config';
import { teamJoinLink, staffJoinLink } from '../../utils/teams';
import { useOrgVoice } from '../../hooks/useOrgVoice';

const howToJoin = (team, hubName, link, isCorporate) => (isCorporate ? [
    `Join ${team} on ${hubName}`,
    '',
    `1. Open this link: ${link}`,
    '2. Pick the role that fits you and enter your name. It takes about five minutes.',
    '3. Family members use the same link and pick their own role. Each has their own private space.'
] : [
    `Join ${team} on ${hubName}`,
    '',
    `1. Open this link: ${link}`,
    '2. Players: pick "Player", then enter your name and age. Under 18? A parent or guardian fills in the consent section with you. It takes about five minutes.',
    '3. Parents: same link, pick "Parent" to get the parent check-in and the talk guides.',
    '',
    'No email for your player? Use yours with a plus tag, like parent+jake@gmail.com.'
]).join('\n');

const howToJoinStaff = (team, hubName, link, isCorporate) => (isCorporate ? [
    `${team} leaders on ${hubName}`,
    '',
    `1. Open this link: ${link}`,
    '2. Sign up with your own work email address.',
    '3. Open the verification email we send; the manager view unlocks after that.',
    '',
    'For managers, supervisors and team leads only. Everyone else uses the team link.'
] : [
    `${team} bench staff on ${hubName}`,
    '',
    `1. Open this link: ${link}`,
    '2. Sign up with your own email address.',
    '3. Open the verification email we send; the team view unlocks after that.',
    '',
    'For coaches, assistants, the manager and other bench staff only. Players and parents use the team link.'
]).join('\n');

const Button = ({ primary, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={primary
            ? 'px-4 py-2.5 bg-brand-primary hover:bg-brand-dark text-white rounded-full font-bold text-sm flex items-center gap-2 transition-colors'
            : 'px-4 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-full font-bold text-sm flex items-center gap-2 transition-colors'}
    >
        {children}
    </button>
);

const TeamJoinLinkCard = ({ orgId, team, hubName }) => {
    const { isCorporate, t } = useOrgVoice();
    const [copied, setCopied] = useState(null);
    // null (not asked) | 'loading' | 'none' (no code set) | 'error' | the link
    const [staff, setStaff] = useState(null);
    const link = teamJoinLink(orgId, team);
    const hub = hubName || 'EAS';
    const text = howToJoin(team, hub, link, isCorporate);
    const copy = async (what, value) => {
        try { await navigator.clipboard.writeText(value); setCopied(what); setTimeout(() => setCopied(null), 1800); } catch (e) { /* no clipboard */ }
    };
    const share = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: `Join ${team}`, text, url: link }); return; } catch (e) { /* cancelled */ }
        }
        copy('link', link);
    };
    const showStaffLink = async () => {
        setStaff('loading');
        try {
            const res = await httpsCallable(functions, 'getStaffJoinCode')({ orgId });
            const code = res.data?.code;
            setStaff(code ? staffJoinLink(orgId, team, code) : 'none');
        } catch (e) { setStaff('error'); }
    };
    const staffLink = staff && !['loading', 'none', 'error'].includes(staff) ? staff : null;

    return (
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8 space-y-5">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-2xl bg-brand-light text-brand-primary flex items-center justify-center shrink-0"><Link2 size={20} /></div>
                    <div className="min-w-0">
                        <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Your team's join link</p>
                        <p className="font-black text-slate-900 leading-tight">{team}</p>
                        <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">{t('joinLinkSub')}</p>
                        <p className="text-[11px] font-mono text-slate-400 mt-2 break-all">{link}</p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                    <Button primary onClick={() => copy('link', link)}>
                        {copied === 'link' ? <Check size={16} /> : <Copy size={16} />} {copied === 'link' ? 'Copied' : 'Copy link'}
                    </Button>
                    <Button onClick={share}><Share2 size={16} /> Share</Button>
                    <Button onClick={() => copy('text', text)}>
                        {copied === 'text' ? <Check size={16} /> : <ClipboardList size={16} />} {copied === 'text' ? 'Copied' : 'Copy "how to join"'}
                    </Button>
                </div>
            </div>

            <div className="border-t border-slate-100 pt-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-2xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><UserCog size={20} /></div>
                    <div className="min-w-0">
                        <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{t('staffLinkTitle')}</p>
                        <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">{t('staffLinkSub')}</p>
                        {staffLink && <p className="text-[11px] font-mono text-slate-400 mt-2 break-all">{staffLink}</p>}
                        {staff === 'none' && <p className="text-xs text-amber-700 font-medium mt-2">{t('staffLinkNone')}</p>}
                        {staff === 'error' && <p className="text-xs text-amber-700 font-medium mt-2">Could not fetch the link just now. Try again in a moment.</p>}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                    {staffLink ? (
                        <>
                            <Button primary onClick={() => copy('staff', staffLink)}>
                                {copied === 'staff' ? <Check size={16} /> : <Copy size={16} />} {copied === 'staff' ? 'Copied' : t('staffLinkCopy')}
                            </Button>
                            <Button onClick={() => copy('stafftext', howToJoinStaff(team, hub, staffLink, isCorporate))}>
                                {copied === 'stafftext' ? <Check size={16} /> : <ClipboardList size={16} />} {copied === 'stafftext' ? 'Copied' : 'Copy "how to join"'}
                            </Button>
                        </>
                    ) : (
                        <Button onClick={showStaffLink}>
                            <UserCog size={16} /> {staff === 'loading' ? 'One moment' : t('staffLinkShow')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TeamJoinLinkCard;
